import { useState, useEffect } from "react";
import { Plus, Trash2, Settings, Loader2 } from "lucide-react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/vibe-code/rcm-v2/components/ui/card";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/vibe-code/rcm-v2/components/ui/select";
import { Label } from "src/vibe-code/rcm-v2/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/vibe-code/rcm-v2/components/ui/alert-dialog";
import { useEmailAutomations } from "src/vibe-code/rcm-api";
import type {
  EmailAutomationRule,
  EmailAutomationTrigger,
} from "src/vibe-code/rcm-v2/types";
import {
  getEmailAutomationTriggerOptions,
  formatEmailAutomationTrigger,
} from "src/vibe-code/rcm-v2/types";
import type { Dealer } from "src/vibe-code/rcm-v2/types";

interface EmailAutomationsProps {
  dealer: Dealer;
  onUpdate: (updatedDealer: Dealer) => void;
  emailAutomationRules: EmailAutomationRule[];
  onEmailRulesUpdate: (updatedRules: EmailAutomationRule[]) => Promise<void>;
  isLoading: boolean;
}

export const EmailAutomations: React.FC<EmailAutomationsProps> = ({
  dealer,
  onUpdate,
  emailAutomationRules,
  onEmailRulesUpdate,
  isLoading,
}) => {
  const { listRules, createRule, deleteRule } = useEmailAutomations();

  const [isCreating, setIsCreating] = useState(false);
  const [rules, setRules] =
    useState<EmailAutomationRule[]>(emailAutomationRules);
  const [selectedTrigger, setSelectedTrigger] = useState<
    EmailAutomationTrigger | ""
  >("");
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [isDeletingRule, setIsDeletingRule] = useState<string | null>(null);

  // Update local rules when prop changes
  useEffect(() => {
    setRules(emailAutomationRules);
  }, [emailAutomationRules]);

  const handleTriggerChange = (triggerDisplay: string) => {
    setSelectedTrigger(triggerDisplay as EmailAutomationTrigger);
  };

  const handleSaveRule = async () => {
    if (!selectedTrigger) return;

    try {
      setIsCreating(true);
      const trigger = selectedTrigger;

      if (editingRule) {
        // For editing, we need to delete the old rule and create a new one
        // since the API doesn't have an update endpoint
        await deleteRule(dealer.id, editingRule);
      }

      const success = await createRule(dealer.id, {
        trigger,
        delay_hours: 0, // Immediate for now
      });

      if (success) {
        // Reload rules to get the updated list
        const updatedRules = await listRules(dealer.id);
        setRules(updatedRules);
        onEmailRulesUpdate(updatedRules);

        const updatedDealer = {
          ...dealer,
          updated_at: new Date().toISOString(),
        };

        onUpdate(updatedDealer);
        resetForm();
      }
    } catch (error) {
      console.error("Failed to save rule:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditRule = (rule: EmailAutomationRule) => {
    setSelectedTrigger(rule.trigger);
    setEditingRule(rule.id);
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      setIsDeletingRule(ruleId);
      const success = await deleteRule(dealer.id, ruleId);

      if (success) {
        const ruleToDelete = rules.find((rule) => rule.id === ruleId);

        // Reload rules to get the updated list
        const updatedRules = await listRules(dealer.id);
        setRules(updatedRules);
        onEmailRulesUpdate(updatedRules);

        const updatedDealer = {
          ...dealer,
          updated_at: new Date().toISOString(),
        };

        onUpdate(updatedDealer);
      }
      setDeleteDialogOpen(null);
    } catch (error) {
      console.error("Failed to delete rule:", error);
    } finally {
      setIsDeletingRule(null);
    }
  };

  const resetForm = () => {
    setSelectedTrigger("");
    setEditingRule(null);
  };

  const getScheduleDisplay = (rule: EmailAutomationRule) => {
    if (rule.delay_hours === 0) {
      return "Send Immediately";
    }
    return `Send after ${rule.delay_hours} hour${rule.delay_hours > 1 ? "s" : ""}`;
  };

  const triggerOptions = getEmailAutomationTriggerOptions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading email automation rules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Configuration Form */}
      <Card className="shadow-custom-md">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>{editingRule ? "Edit" : "Create"} Email Automation Rule</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Module 1: DigiFi Step Selection */}
          <div className="space-y-2">
            <Label>DigiFi Step</Label>
            <Select
              value={selectedTrigger}
              onValueChange={handleTriggerChange}
              disabled={isCreating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select DigiFi Step" />
              </SelectTrigger>
              <SelectContent>
                {triggerOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Save Button */}
          <div className="flex items-center space-x-2 pt-4">
            <Button
              onClick={handleSaveRule}
              disabled={!selectedTrigger || isCreating}
              className="flex items-center space-x-2"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              <span>{editingRule ? "Update Rule" : "Save Rule"}</span>
            </Button>
            {editingRule && (
              <Button
                variant="outline"
                onClick={resetForm}
                disabled={isCreating}
              >
                Cancel Edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Saved Rules */}
      <Card className="shadow-custom-md">
        <CardHeader>
          <CardTitle>Saved Automation Rules ({rules.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {rules.length > 0 ? (
            <div className="space-y-4">
              {rules.map((rule) => {
                const triggerDisplay = formatEmailAutomationTrigger(
                  rule.trigger,
                );

                return (
                  <div key={rule.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <Badge variant="default">{triggerDisplay}</Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {getScheduleDisplay(rule)}
                          {!rule.is_active && (
                            <Badge variant="outline" className="ml-2">
                              Inactive
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditRule(rule)}
                          disabled={isCreating || isDeletingRule === rule.id}
                        >
                          Edit
                        </Button>
                        <AlertDialog
                          open={deleteDialogOpen === rule.id}
                          onOpenChange={(open: boolean) =>
                            setDeleteDialogOpen(open ? rule.id : null)
                          }
                        >
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={
                                isCreating || isDeletingRule === rule.id
                              }
                            >
                              {isDeletingRule === rule.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Are you sure you want to delete this automation
                                rule?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. This will
                                permanently delete the email automation rule for
                                "{triggerDisplay}" and it will no longer trigger
                                automated emails.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteRule(rule.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Yes, Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No automation rules configured yet
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first rule above to get started
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card className="shadow-custom-md">
        <CardHeader>
          <CardTitle className="text-lg">How Email Automations Work</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <p>
              <strong>Trigger:</strong> When a dealer's DigiFi Step is updated
              anywhere in the app, the system will check for matching automation
              rules.
            </p>
            <p>
              <strong>Recipients:</strong> Automated emails will be sent to all
              contacts associated with this dealer.
            </p>
            <p>
              <strong>Activity Logging:</strong> All triggered emails are
              automatically logged in the dealer's Activity tab with timestamps
              and status references.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
